import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { useDeleteBiddingIntradayOrders } from '~/hooks/bidding/useDeleteBiddingIntradayOrders'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { isWorkflowCaseSuspended } from '~/models/workflowCaseReadonly'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradayBulkDeleteRefusedBid,
  intradayBulkDeleteBlockedCause,
  isIntradayBulkDeletableBid,
  readIntradayBulkDeleteResponse,
} from '~/pages/IntradayBidding/models/intradayBulkDelete'
import { intradayBulkRequestTargets } from '~/pages/IntradayBidding/models/intradayBulkRequestTargets'
import { scopeToListedBids } from '~/pages/IntradayBidding/models/intradayListScope'

const KEY = 'page:biddingIntraday.bulkDelete'

/** 削除しなかった札のバナーの中身。 受渡日ごとの結果なので受渡日を持つ。 */
export type IntradayBulkDeleteRefused = {
  deletedCount: number
  bids: IntradayBulkDeleteRefusedBid[]
  targetDate: Date
}

/** 確認ダイアログを開いた時に数えた対象。 開いている間に一覧が変わっても変えない。 */
export type IntradayBulkDeleteConfirmScope = {
  targets: readonly IntradayBidRow[]
  /** 絞り込みで表示していない下書きの件数 (「対象外」 の行)。 */
  hiddenCount: number
}

/** 入札を全削除 (AC-BIN-138) の、 ⋯メニュー項目・確認ダイアログ・バナーの状態。 */
export type IntradayBulkDelete = {
  /** ⋯メニュー項目を選べるか。 */
  selectable: boolean
  /**
   * 項目の hover / 強調で出すツールチップ = 選べない理由。 選べる時、 一覧の取得前・読み取り専用の
   * 理由の取得中は undefined。
   */
  tooltip: string | undefined
  /** 確認ダイアログを開く (対象をこの時点の一覧で固定する)。 */
  open: () => void
  /** 確認ダイアログで確認している対象。 undefined の間は閉じている。 */
  confirmScope: IntradayBulkDeleteConfirmScope | undefined
  isSubmitting: boolean
  confirm: () => Promise<void>
  cancel: () => void
  /** 削除しなかった札のバナー。 */
  refused: IntradayBulkDeleteRefused | undefined
  dismissRefused: () => void
}

type Params = {
  /** 選択中の受渡日の全札 (絞り込む前)。 */
  allBids: readonly IntradayBidRow[]
  /** 一覧に表示中の札 (フィルタと売買逆転のみ表示の後。 提出の対象と同じ範囲)。 */
  listedBids: readonly IntradayBidRow[]
  /** 一覧を取得できているか (初めての読み込み中・取得の失敗は false)。 */
  isListLoaded: boolean
  /** 完了済み・削除済みのワークフローから開いている (AC-WFW-014)。 */
  isReadonly: boolean
  /** 読み取り専用の理由 (`useWorkflowLockReasons`。 取得中は undefined)。 */
  readonlyReason: string | undefined
  targetDate: Date
}

/**
 * 入札を全削除 (AC-BIN-138)。
 *
 * - 対象は一覧に表示中の下書きの札。 確認ダイアログを開いた時点の一覧で固定し、 開いている間に
 *   一覧が変わっても件数と送る札は変えない (その後に作られた下書きは送らない)
 * - 札ごとに id と、 一覧が持つ札の更新日時を Timestamp のまま送り、 backend が札ごとに照合する
 * - 全部削除できたら成功のトースト。 削除しなかった札があれば成功のトーストに代えてエラーバナー
 * - 要求の全体が通らなかった時 (通信の失敗・ワークフローの完了 等) は、 どの札も削除していない
 */
export function useIntradayBulkDelete({
  allBids,
  listedBids,
  isListLoaded,
  isReadonly,
  readonlyReason,
  targetDate,
}: Params): IntradayBulkDelete {
  const { t } = useTranslation()
  const { wfCase } = useCurrentWorkflowCase()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()
  const { mutateAsync, isPending } = useDeleteBiddingIntradayOrders()
  const [confirmScope, setConfirmScope] = useState<
    IntradayBulkDeleteConfirmScope | undefined
  >(undefined)
  const [refused, setRefused] = useState<IntradayBulkDeleteRefused | undefined>(
    undefined,
  )

  const scope = scopeToListedBids(
    allBids,
    listedBids,
    isIntradayBulkDeletableBid,
  )
  const blockedCause = intradayBulkDeleteBlockedCause({
    bidCount: allBids.length,
    draftCount: scope.existingCount,
    listedDraftCount: scope.targets.length,
  })
  // 完了済み・削除済み (読み取り専用) と中断は、 下書きの件数に関わらず選べない (AC-BIN-138 / AC-BIN-134)。
  // 中断は共有の読み取り専用の判定に含まれないので、 この項目で足す。
  const isSuspended = wfCase !== undefined && isWorkflowCaseSuspended(wfCase)
  const isWorkflowLocked = isReadonly || isSuspended
  const selectable =
    !isWorkflowLocked && isListLoaded && blockedCause === undefined

  // 複数に当たる時はワークフローの理由 → 入札 0 件 → 下書き 0 件 → 絞り込みの順で 1 つだけ出す。
  // 一覧の取得前は件数が決まらないので理由を出さない (一覧の読み込み中・取得失敗の表示に任せる)。
  const tooltip = isReadonly
    ? readonlyReason
    : isSuspended
      ? t('common:workflowLock.suspendedReason')
      : !isListLoaded || blockedCause === undefined
        ? undefined
        : t(`${KEY}.menu.${blockedCause}Reason`)

  const confirm = async (): Promise<void> => {
    if (confirmScope === undefined || confirmScope.targets.length === 0) return
    const sending = confirmScope.targets
    // 前の結果のバナーは、 次の全削除で置き換える (要求の全体が失敗した時も古い結果を残さない)。
    setRefused(undefined)
    let response: Awaited<ReturnType<typeof mutateAsync>>
    try {
      response = await mutateAsync({
        targets: intradayBulkRequestTargets(sending),
      })
    } catch {
      showErrorToast({ title: t('page:biddingIntraday.deleteConfirm.error') })
      setConfirmScope(undefined)
      return
    }
    const outcome = readIntradayBulkDeleteResponse(response, sending)
    if (outcome.refused.length === 0) {
      showSuccessToast({ title: t(`${KEY}.successToast`) })
    } else {
      setRefused({
        deletedCount: outcome.deletedCount,
        bids: outcome.refused,
        targetDate,
      })
    }
    setConfirmScope(undefined)
  }

  return {
    selectable,
    tooltip,
    open: () => {
      if (!selectable) return
      setConfirmScope({
        targets: scope.targets,
        hiddenCount: scope.hiddenCount,
      })
    },
    confirmScope,
    isSubmitting: isPending,
    confirm,
    cancel: () => {
      if (isPending) return
      setConfirmScope(undefined)
    },
    refused,
    dismissRefused: () => setRefused(undefined),
  }
}
